import {FormEventHandler, useEffect, useRef} from 'react';
import GuestLayoutCompact from '@/Layouts/Guest/GuestLayoutCompact';
import InputError from '@/Components/InputError';
import InputLabel from '@/Components/InputLabel';
import PrimaryButton from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import {Head, Link, useForm} from '@inertiajs/react';
import GoogleSignInButton from "@/Components/GoogleSignInButton";
import useTranslations from "@/hooks/useTranslations";

type RegisterPageProps = {
    googleOauthEnabled: boolean;
    turnstileSiteKey: string;
}

function loadTurnstileScript() {
    if (document.getElementById('cf-turnstile-script')) return;

    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad';
    script.defer = true;
    document.head.appendChild(script);
}

export default function Register({googleOauthEnabled, turnstileSiteKey}: RegisterPageProps) {
    const { __ } = useTranslations();
    const {data, setData, post, processing, errors, reset} = useForm({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        'cf-turnstile-response': '',
    });
    const turnstileRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        return () => {
            reset('password', 'password_confirmation');
        };
    }, []);

    useEffect(() => {
        if (!turnstileSiteKey) return;

        let mounted = true;
        let widgetId: string | undefined;

        const renderWidget = () => {
            if (!mounted || !turnstileRef.current) return;
            widgetId = window.turnstile!.render(turnstileRef.current, {
                sitekey: turnstileSiteKey,
                callback: (token: string) => setData('cf-turnstile-response', token),
            });
        };

        if (window.turnstile) {
            renderWidget();
        } else {
            window.onTurnstileLoad = renderWidget;
            loadTurnstileScript();
        }

        return () => {
            mounted = false;
            if (widgetId) window.turnstile?.remove(widgetId);
        };
    }, []);

    const submit: FormEventHandler = (e) => {
        e.preventDefault();
        post(route('register'));
    };

    return (
        <GuestLayoutCompact>
            <Head title={__('register.title')}>
                <meta name="description"
                      content={__('register.meta_description')}/>
            </Head>
            {
                googleOauthEnabled && (
                    <>
                        <div className="flex justify-center mt-5 ">
                            <a href={route('auth.redirect')} className="w-full block">
                                <GoogleSignInButton/>
                            </a>
                        </div>
                        <hr className="my-8"/>
                    </>
                )
            }


            <form onSubmit={submit}>
                <div>
                    <InputLabel htmlFor="name" value={__('register.name')}/>

                    <TextInput
                        id="name"
                        name="name"
                        value={data.name}
                        className="mt-1 block w-full"
                        autoComplete="name"
                        isFocused={true}
                        onChange={(e) => setData('name', e.target.value)}
                        required
                    />

                    <InputError message={errors.name} className="mt-2"/>
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="email" value={__('register.email')}/>

                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="mt-1 block w-full"
                        autoComplete="username"
                        onChange={(e) => setData('email', e.target.value)}
                        required
                    />

                    <InputError message={errors.email} className="mt-2"/>
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="password" value={__('register.password')}/>

                    <TextInput
                        id="password"
                        type="password"
                        name="password"
                        value={data.password}
                        className="mt-1 block w-full"
                        autoComplete="new-password"
                        onChange={(e) => setData('password', e.target.value)}
                        required
                    />

                    <InputError message={errors.password} className="mt-2"/>
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="password_confirmation" value={__('register.confirm_password')}/>

                    <TextInput
                        id="password_confirmation"
                        type="password"
                        name="password_confirmation"
                        value={data.password_confirmation}
                        className="mt-1 block w-full"
                        autoComplete="new-password"
                        onChange={(e) => setData('password_confirmation', e.target.value)}
                        required
                    />

                    <InputError message={errors.password_confirmation} className="mt-2"/>
                </div>

                {turnstileSiteKey && (
                    <div className="mt-4">
                        <div ref={turnstileRef}/>
                        <InputError message={errors['cf-turnstile-response']} className="mt-2"/>
                    </div>
                )}

                <div className="flex items-center justify-end mt-4">
                    <Link
                        href={route('login')}
                        className="underline text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        {__('register.already_registered')}
                    </Link>

                    <PrimaryButton className="ms-4" disabled={processing}>
                        {__('register.submit')}
                    </PrimaryButton>
                </div>
            </form>
        </GuestLayoutCompact>
    );
}
